/**
 * IframeContainer - Reusable iframe component for embedding external content
 * 
 * This component provides a consistent way to embed iframes across all Konnect xD apps.
 * It handles responsive sizing, loading states, and error handling.
 * 
 * @example
 * ```tsx
 * <IframeContainer
 *   src="https://example.com"
 *   title="External Content"
 *   height="100%"
 * />
 * ```
 */

import React, { forwardRef } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';

export interface IframeContainerProps {
  /** The URL to load in the iframe */
  src: string;
  
  /** Accessible title for the iframe */
  title: string;
  
  /** Height of the iframe (default: '100%') */
  height?: string | number;
  
  /** Width of the iframe (default: '100%') */
  width?: string | number;
  
  /** Whether to allow fullscreen (default: true) */
  allowFullscreen?: boolean;
  
  /** Additional iframe sandbox permissions */
  sandbox?: string;
  
  /** Callback when iframe loads successfully */
  onLoad?: () => void;
  
  /** Callback when iframe fails to load */
  onError?: () => void;
  
  /** Show loading indicator (default: true) */
  showLoading?: boolean;
  
  /** Custom loading component */
  loadingComponent?: React.ReactNode;
  
  /** Additional CSS class name */
  className?: string;
  
  /** Additional inline styles */
  style?: React.CSSProperties;
  
  /** Whether to enable scrolling in the iframe (default: 'yes') */
  scrolling?: 'yes' | 'no' | 'auto';
}

const IframeContainer = forwardRef<HTMLIFrameElement, IframeContainerProps>(({
  src,
  title,
  height = '100%',
  width = '100%',
  allowFullscreen = true,
  sandbox,
  onLoad,
  onError,
  showLoading = true,
  loadingComponent,
  className,
  style,
  scrolling = 'yes',
}, ref) => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasError, setHasError] = React.useState(false);

  const handleLoad = () => {
    setIsLoading(false);
    onLoad?.();
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
    onError?.();
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width,
        height,
        overflow: scrolling === 'no' ? 'hidden' : 'auto',
        backgroundColor: 'background.default',
        ...style,
      }}
      className={className}
    >
      {/* Loading Indicator */}
      {showLoading && isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'background.default',
            zIndex: 1,
          }}
        >
          {loadingComponent || (
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                Loading content...
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Error State */}
      {hasError && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'background.default',
            zIndex: 1,
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="error" gutterBottom>
              Failed to Load Content
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Unable to load the requested content. Please try again later.
            </Typography>
          </Box>
        </Box>
      )}

      {/* Iframe */}
      {!hasError && (
        <iframe
          ref={ref}
          src={src}
          title={title}
          width="100%"
          height="100%"
          frameBorder="0"
          scrolling={scrolling}
          allowFullScreen={allowFullscreen}
          sandbox={sandbox}
          onLoad={handleLoad}
          onError={handleError}
          style={{
            border: 'none',
            display: 'block',
          }}
        />
      )}
    </Box>
  );
});

IframeContainer.displayName = 'IframeContainer';

export default IframeContainer;
